import { useCallback, useEffect, useRef, useState } from "react";
import rosteringApi from "store/services/rostering.services";
import { formatSilHomeLabel } from "../components/advanceModeSplitDefaults";
import {
  isRequestCancelled,
  replaceAbortController,
} from "Utils/requestCancel";

const PER_PAGE = 10;
const DEBOUNCE_MS = 500;

const mapHomeToOption = (home) => ({
  ...home,
  label: formatSilHomeLabel(home),
  value: home.id,
  uuid: home.uuid,
});

export const extractSilHomesPage = (response) => {
  const root = response?.data ?? response;
  if (root?.data && Array.isArray(root.data)) {
    return {
      rows: root.data,
      currentPage: root.current_page || 1,
      lastPage: root.total_pages || 1,
    };
  }
  if (Array.isArray(root)) {
    return { rows: root, currentPage: 1, lastPage: 1 };
  }
  return { rows: [], currentPage: 1, lastPage: 1 };
};

/**
 * Resolve a saved sil_home_id to a full select option (uuid + label).
 * The V5 shift GET returns only sil_home_id — not the nested home — so edit
 * mode must look up the home from the sil-homes list API.
 */
export const fetchSilHomeOptionById = async (homeId, options = {}) => {
  if (homeId == null || homeId === "") return null;
  const targetId = Number(homeId);
  if (!Number.isFinite(targetId)) return null;

  let page = 1;
  const perPage = 50;
  const maxPages = 20;
  const signal = options.signal;

  while (page <= maxPages) {
    if (signal?.aborted) return null;
    const response = await rosteringApi.getSilHomes({
      page,
      per_page: perPage,
      search: "",
      sort_field: "street_address",
      sort_order: "asc",
      signal,
    });
    const { rows, currentPage, lastPage } = extractSilHomesPage(response);
    const found = rows.find((home) => Number(home.id) === targetId);
    if (found) return mapHomeToOption(found);
    if (currentPage >= lastPage || rows.length === 0) break;
    page += 1;
  }

  return null;
};

/**
 * SIL homes list (v5 sil-homes) — load on menu open, server search, scroll pages of 10.
 */
export const useAdvanceSilHomesSearch = (selectedHome = null) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  const mergeSelected = useCallback(
    (list) => {
      if (!selectedHome?.value) return list;
      const exists = list.some((o) => o.value === selectedHome.value);
      if (exists) return list;
      return [selectedHome, ...list];
    },
    [selectedHome]
  );

  const fetchPage = useCallback(
    async (search, page, append) => {
      const requestId = ++requestIdRef.current;
      abortControllerRef.current = replaceAbortController(
        abortControllerRef.current
      );
      const signal = abortControllerRef.current?.signal;
      setIsLoading(true);

      try {
        const response = await rosteringApi.getSilHomes({
          page,
          per_page: PER_PAGE,
          search: search || "",
          sort_field: "street_address",
          sort_order: "asc",
          signal,
        });

        if (requestId !== requestIdRef.current) return;

        const { rows, currentPage: pageNum, lastPage } = extractSilHomesPage(response);
        const mapped = rows.map(mapHomeToOption);

        setOptions((prev) => {
          const combined = append ? [...prev, ...mapped] : mapped;
          const deduped = [];
          const seen = new Set();
          combined.forEach((o) => {
            if (!seen.has(o.value)) {
              seen.add(o.value);
              deduped.push(o);
            }
          });
          return mergeSelected(deduped);
        });

        setHasMore(pageNum < lastPage);
        setCurrentPage(pageNum);
      } catch (err) {
        if (isRequestCancelled(err)) return;
        if (requestId === requestIdRef.current && !append) {
          setOptions(mergeSelected([]));
          setHasMore(false);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [mergeSelected]
  );

  const handleMenuOpen = useCallback(() => {
    searchRef.current = "";
    setCurrentPage(1);
    fetchPage("", 1, false);
  }, [fetchPage]);

  const handleInputChange = useCallback(
    (inputValue) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        searchRef.current = inputValue || "";
        setCurrentPage(1);
        fetchPage(inputValue || "", 1, false);
      }, DEBOUNCE_MS);
    },
    [fetchPage]
  );

  const handleMenuScrollToBottom = useCallback(() => {
    if (isLoading || !hasMore) return;
    fetchPage(searchRef.current, currentPage + 1, true);
  }, [isLoading, hasMore, currentPage, fetchPage]);

  const selectedKey = selectedHome?.value ?? "";

  useEffect(() => {
    setOptions((prev) => mergeSelected(prev));
  }, [selectedKey, mergeSelected]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestIdRef.current += 1;
      try {
        abortControllerRef.current?.abort?.();
      } catch {
        // ignore
      }
    },
    []
  );

  return {
    options,
    isLoading,
    hasMore,
    handleMenuOpen,
    handleInputChange,
    handleMenuScrollToBottom,
  };
};

export default useAdvanceSilHomesSearch;
